import {
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  ProfileCompletenessConfig,
  ProfileCompletenessConfigFirestore,
  FieldCompletenessConfig,
  CompletenessField,
} from '../types/admin';
import { PersonFormData, LocationFormData } from '../types';

/**
 * ProfileValidationService - Manages profile data validation and completeness calculation
 *
 * This service handles:
 * - Validating and normalizing profile data (e.g., auto-marking deceased if death date exists)
 * - Calculating profile completeness based on configurable field weights
 * - Managing field configuration for completeness calculations
 */
export class ProfileValidationService {
  private static readonly COLLECTION_NAME = 'profile_completeness_config';
  private static readonly CONFIG_DOC_ID = 'default';
  private static cachedConfig: ProfileCompletenessConfig | null = null;
  private static cacheExpiry: number = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Validate and normalize profile data
   * This ensures data consistency before saving or calculating completeness
   */
  static validateAndNormalize(data: PersonFormData): PersonFormData {
    const normalized = { ...data };

    // Rule: If death date or death place exists, person must be marked as deceased
    const hasDeathDate = !!(normalized.deathDate?.trim());
    const hasDeathPlace = this.hasLocationData(normalized.deathPlace);

    if ((hasDeathDate || hasDeathPlace) && !normalized.isDeceased) {
      normalized.isDeceased = true;
    }

    // Rule: If marked as not deceased, clear death-related fields
    if (!normalized.isDeceased) {
      normalized.deathDate = '';
      normalized.deathPlace = {}; // Empty location (all fields optional)
    }

    return normalized;
  }

  /**
   * Get the default field configuration
   */
  private static getDefaultConfig(userId: string, userName: string): ProfileCompletenessConfig {
    const defaultFields: FieldCompletenessConfig[] = [
      // Basic Information (high weight)
      { field: 'firstName', label: 'First Name', enabled: true, weight: 15, category: 'basic' },
      { field: 'lastName', label: 'Last Name', enabled: true, weight: 15, category: 'basic' },
      { field: 'gender', label: 'Gender', enabled: true, weight: 10, category: 'basic' },

      // Dates (high weight)
      { field: 'birthDate', label: 'Birth Date', enabled: true, weight: 12, category: 'dates' },
      { field: 'deathDate', label: 'Death Date', enabled: true, weight: 8, category: 'dates' },
      { field: 'isDeceased', label: 'Deceased Status', enabled: true, weight: 5, category: 'dates' },

      // Locations (medium weight)
      { field: 'birthPlace', label: 'Birth Place', enabled: true, weight: 10, category: 'locations' },
      { field: 'deathPlace', label: 'Death Place', enabled: true, weight: 8, category: 'locations' },
      { field: 'currentLocation', label: 'Current Location', enabled: false, weight: 5, category: 'locations' },

      // Biographical (medium weight)
      { field: 'occupation', label: 'Occupation', enabled: true, weight: 7, category: 'biographical' },
      { field: 'education', label: 'Education', enabled: true, weight: 6, category: 'biographical' },
      { field: 'biography', label: 'Biography', enabled: true, weight: 8, category: 'biographical' },

      // Additional Information (low weight)
      { field: 'maidenName', label: 'Maiden Name', enabled: true, weight: 4, category: 'additional' },
      { field: 'middleName', label: 'Middle Name', enabled: true, weight: 3, category: 'additional' },
      { field: 'nicknames', label: 'Nicknames', enabled: true, weight: 2, category: 'additional' },
      { field: 'suffix', label: 'Suffix', enabled: false, weight: 1, category: 'additional' },
      { field: 'personalNotes', label: 'Personal Notes', enabled: true, weight: 5, category: 'additional' },
    ];

    return {
      id: this.CONFIG_DOC_ID,
      fields: defaultFields,
      updatedAt: new Date(),
      updatedBy: userId,
      updatedByName: userName,
    };
  }

  /**
   * Get the current configuration from Firestore (with caching)
   */
  static async getConfig(): Promise<ProfileCompletenessConfig> {
    try {
      // Return cached config if still valid
      if (this.cachedConfig && Date.now() < this.cacheExpiry) {
        return this.cachedConfig;
      }

      const configRef = doc(db, this.COLLECTION_NAME, this.CONFIG_DOC_ID);
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        const firestoreData = configSnap.data() as ProfileCompletenessConfigFirestore;
        const config: ProfileCompletenessConfig = {
          ...firestoreData,
          updatedAt: firestoreData.updatedAt.toDate(),
        };

        // Cache the config
        this.cachedConfig = config;
        this.cacheExpiry = Date.now() + this.CACHE_TTL;

        return config;
      }

      // No config exists, create default
      const defaultConfig = this.getDefaultConfig('system', 'System');

      // Try to save the default config, but if it fails (e.g., permissions), just return it
      try {
        await this.saveConfig(defaultConfig, 'system', 'System');
      } catch (saveError) {
        console.warn('Could not save default profile completeness config to Firestore:', saveError);
        // Still return the default config so the app can function
      }

      return defaultConfig;
    } catch (error) {
      console.error('Error loading profile completeness config:', error);
      // If we have a cached config, return it even if expired
      if (this.cachedConfig) {
        console.warn('Using expired cached config due to error');
        return this.cachedConfig;
      }
      // Last resort: return default config without saving
      return this.getDefaultConfig('system', 'System');
    }
  }

  /**
   * Save configuration to Firestore
   */
  static async saveConfig(
    config: ProfileCompletenessConfig,
    userId: string,
    userName: string
  ): Promise<void> {
    const configRef = doc(db, this.COLLECTION_NAME, this.CONFIG_DOC_ID);

    const firestoreData: Omit<ProfileCompletenessConfigFirestore, 'id'> = {
      fields: config.fields,
      updatedAt: serverTimestamp() as any,
      updatedBy: userId,
      updatedByName: userName,
    };

    await setDoc(configRef, firestoreData);

    // Invalidate cache
    this.cachedConfig = null;
    this.cacheExpiry = 0;
  }

  /**
   * Check if a LocationFormData object has any actual data
   */
  private static hasLocationData(location: LocationFormData | undefined): boolean {
    if (!location) return false;
    return !!(location.city?.trim() || location.state?.trim() || location.country?.trim());
  }

  /**
   * Get the value of a field from PersonFormData
   * NOTE: This method assumes the field is applicable to the person.
   * Non-applicable fields should be filtered out by isFieldApplicable() before calling this.
   */
  private static getFieldValue(data: PersonFormData, field: CompletenessField): any {
    switch (field) {
      case 'deathDate':
        // Only called for deceased people (filtered by isFieldApplicable)
        return data.deathDate?.trim() || null;

      case 'deathPlace':
        // Only called for deceased people (filtered by isFieldApplicable)
        return this.hasLocationData(data.deathPlace) ? data.deathPlace : null;

      case 'birthPlace':
      case 'currentLocation':
        return this.hasLocationData(data[field]) ? data[field] : null;

      case 'nicknames':
        // nicknames in PersonFormData is a string (comma-separated)
        return data.nicknames?.trim() || null;

      default:
        const value = data[field as keyof PersonFormData];
        // Check for empty strings
        if (typeof value === 'string') {
          return value.trim() || null;
        }
        return value;
    }
  }

  /**
   * Check if a field is applicable to this person
   * Some fields are conditional (e.g., death fields only apply to deceased people)
   */
  private static isFieldApplicable(data: PersonFormData, field: CompletenessField): boolean {
    // Death-related fields only apply to deceased people
    if (field === 'deathDate' || field === 'deathPlace') {
      return data.isDeceased;
    }
    // All other fields always apply
    return true;
  }

  /**
   * Calculate profile completeness based on current configuration
   * Data is automatically validated and normalized before calculation
   */
  static async calculateCompleteness(data: PersonFormData): Promise<number> {
    // IMPORTANT: Validate and normalize data FIRST
    const normalizedData = this.validateAndNormalize(data);

    const config = await this.getConfig();

    // Filter to only enabled fields
    const enabledFields = config.fields.filter(f => f.enabled);

    if (enabledFields.length === 0) {
      return 0; // No fields configured
    }

    // Calculate total weight ONLY for applicable fields
    const applicableFields = enabledFields.filter(f => this.isFieldApplicable(normalizedData, f.field));
    const totalWeight = applicableFields.reduce((sum, field) => sum + field.weight, 0);

    if (totalWeight === 0) {
      return 0; // No weight configured
    }

    // Calculate earned weight ONLY for applicable fields
    let earnedWeight = 0;
    applicableFields.forEach(fieldConfig => {
      const value = this.getFieldValue(normalizedData, fieldConfig.field);
      if (value !== null && value !== undefined) {
        earnedWeight += fieldConfig.weight;
      }
    });

    // Return percentage
    return Math.round((earnedWeight / totalWeight) * 100);
  }

  /**
   * Get a summary of which fields are filled vs missing
   */
  static async getCompletionSummary(data: PersonFormData): Promise<{
    totalFields: number;
    filledFields: number;
    missingFields: string[];
    filledByCategory: Record<string, number>;
    totalByCategory: Record<string, number>;
  }> {
    // Validate and normalize data first
    const normalizedData = this.validateAndNormalize(data);

    const config = await this.getConfig();
    const enabledFields = config.fields.filter(f => f.enabled);

    const missingFields: string[] = [];
    const filledByCategory: Record<string, number> = {};
    const totalByCategory: Record<string, number> = {};

    enabledFields.forEach(fieldConfig => {
      const category = fieldConfig.category;

      // Initialize category counters
      if (!totalByCategory[category]) {
        totalByCategory[category] = 0;
        filledByCategory[category] = 0;
      }

      totalByCategory[category]++;

      const value = this.getFieldValue(normalizedData, fieldConfig.field);
      if (value !== null && value !== undefined) {
        filledByCategory[category]++;
      } else {
        missingFields.push(fieldConfig.label);
      }
    });

    return {
      totalFields: enabledFields.length,
      filledFields: enabledFields.length - missingFields.length,
      missingFields,
      filledByCategory,
      totalByCategory,
    };
  }

  /**
   * Validate field configuration
   */
  static validateConfig(config: ProfileCompletenessConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check that at least one field is enabled
    const enabledCount = config.fields.filter(f => f.enabled).length;
    if (enabledCount === 0) {
      errors.push('At least one field must be enabled');
    }

    // Check weights are within bounds
    config.fields.forEach(field => {
      if (field.weight < 0 || field.weight > 100) {
        errors.push(`Weight for ${field.label} must be between 0 and 100`);
      }
    });

    // Check total weight doesn't exceed reasonable limits
    const totalWeight = config.fields.filter(f => f.enabled).reduce((sum, f) => sum + f.weight, 0);
    if (totalWeight === 0 && enabledCount > 0) {
      errors.push('Total weight of enabled fields must be greater than 0');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Clear the cache (useful for testing or forcing refresh)
   */
  static clearCache(): void {
    this.cachedConfig = null;
    this.cacheExpiry = 0;
  }
}
